/* ===========================================================================
   Universal Security Instruments — "Spec Sheet" button → in-page modal
   ---------------------------------------------------------------------------
   Theme asset: assets/usi-specsheet-modal.js

   WHAT THIS DOES
     Today, the "Spec Sheet" link on a product page is a plain anchor:
       <a href="{{ product.url }}?view=spec-sheet" target="_blank">Spec Sheet</a>
     which opens the spec-sheet as a brand-new full-page tab (confirmed live,
     Sept 2026 — the "Spec Sheet" card in the .rte.mgrid-container block on
     the PDP). This script intercepts clicks on that link, cancels the
     navigation/new-tab, and instead:
       1. fetches the same "?view=spec-sheet" URL in the background,
       2. parses the returned HTML,
       3. pulls out #dvContainer2 (the real spec-sheet root — see
          usi-specsheet-pdf.js for how that was confirmed against the live
          DOM), along with any <link>/<style> tags the spec-sheet view needs
          that aren't already on the current page,
       4. injects it all into an on-page modal/overlay.

     Because the injected markup is the exact same #dvContainer2 subtree the
     full-page view renders, the "Download Spec Sheet (PDF)" button installed
     via usi-specsheet-pdf-button.liquid keeps working unchanged inside the
     modal — assets/usi-specsheet-pdf.js's click delegate just needs to
     already be loaded on the page (see the INSTALL note at the bottom).

   WHY FETCH RATHER THAN JUST TOGGLE VISIBILITY
     #dvContainer2 is rendered by a *different* view of the same product URL
     (?view=spec-sheet), not present anywhere in the normal PDP's DOM — so
     there's nothing to just show/hide locally; the content has to be
     fetched from that view once and cached.

   DEPENDENCY
     None at parse time. Requires assets/usi-specsheet-pdf.js to be loaded
     somewhere on the page (any time before the user clicks the in-modal
     download button) for the PDF button to function — see INSTALL below.
   =========================================================================== */

(function () {
  'use strict';

  var TRIGGER_HREF_MATCH = /[?&]view=spec-sheet\b/i;
  var CONTENT_SELECTOR = '#dvContainer2';
  var MODAL_ID = 'usi-specsheet-modal';

  var cache = Object.create(null); // href → Promise<{node, assetHrefs}>
  var modalEl = null;
  var lastFocused = null;

  /* ---------------------------------------------------------------------
     Modal shell — built once, reused for every open.
     --------------------------------------------------------------------- */

  function ensureModal() {
    if (modalEl) return modalEl;

    modalEl = document.createElement('div');
    modalEl.id = MODAL_ID;
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-label', 'Product spec sheet');
    modalEl.hidden = true;
    modalEl.innerHTML =
      '<div class="usi-specsheet-modal__backdrop" data-usi-specsheet-close></div>' +
      '<div class="usi-specsheet-modal__panel">' +
        '<button type="button" class="usi-specsheet-modal__close" data-usi-specsheet-close aria-label="Close spec sheet">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
        '</button>' +
        '<div class="usi-specsheet-modal__body" data-usi-specsheet-body>' +
          '<div class="usi-specsheet-modal__loading" data-usi-specsheet-loading>Loading spec sheet…</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(modalEl);

    modalEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-usi-specsheet-close]')) closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modalEl.hidden) closeModal();
    });

    injectModalStyles();
    return modalEl;
  }

  function injectModalStyles() {
    if (document.querySelector('style[data-usi-specsheet-modal-styles]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-usi-specsheet-modal-styles', 'true');
    style.textContent =
      '#' + MODAL_ID + '{position:fixed;inset:0;z-index:9999;}' +
      '#' + MODAL_ID + '[hidden]{display:none;}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__backdrop{position:absolute;inset:0;background:rgba(20,17,18,.55);}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__panel{position:relative;box-sizing:border-box;width:min(1200px,96vw);max-height:90vh;margin:5vh auto;background:#fff;border-radius:8px;box-shadow:0 20px 60px rgba(0,0,0,.35);overflow:hidden;display:flex;flex-direction:column;}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__body{overflow-y:auto;-webkit-overflow-scrolling:touch;}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__body .page-width{max-width:none;}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__close{position:absolute;top:10px;right:10px;z-index:2;width:36px;height:36px;border:none;border-radius:50%;background:#fff;color:#1a1718;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.25);}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__close:hover{background:#f2f2f2;}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__loading,#' + MODAL_ID + ' .usi-specsheet-modal__error{padding:64px 32px;text-align:center;font:14px/1.5 Arial,Helvetica,sans-serif;color:#555;}' +
      '#' + MODAL_ID + ' .usi-specsheet-modal__error a{color:#1a1718;font-weight:700;}' +
      'body.usi-specsheet-modal-open{overflow:hidden;}';
    document.head.appendChild(style);
  }

  function openModal() {
    ensureModal();
    lastFocused = document.activeElement;
    modalEl.hidden = false;
    document.body.classList.add('usi-specsheet-modal-open');
    var closeBtn = modalEl.querySelector('.usi-specsheet-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modalEl || modalEl.hidden) return;
    modalEl.hidden = true;
    document.body.classList.remove('usi-specsheet-modal-open');
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function setBody(html) {
    var body = modalEl.querySelector('[data-usi-specsheet-body]');
    body.innerHTML = html;
  }

  /* ---------------------------------------------------------------------
     Fetch + parse the spec-sheet view, cached per URL.
     --------------------------------------------------------------------- */

  function ensureHeadAssets(parsedDoc) {
    /* Bring over any stylesheet <link> AND inline <style> tags the fetched
       view depends on that aren't already on the current page — dedupe by
       href (links) or exact text content (style blocks) so repeat opens
       don't double-inject.

       The <style> half matters a lot here: the spec-sheet section's own
       markup puts a <style> block (defining .specSku's pill look, and the
       on-screen spec-row grid rules) as a SIBLING of #dvContainer2, not a
       child — so grabbing just #dvContainer2 (as fetchSection() does below)
       silently drops it. Without it, the SKU renders as plain text instead
       of the grey pill, and long labels like "Interconnectivity" can
       collide with their value column. Copying every <style> tag in the
       parsed document (not just ones inside #dvContainer2) fixes both. */
    var head = document.head;

    var existingHrefs = Object.create(null);
    Array.prototype.forEach.call(head.querySelectorAll('link[rel="stylesheet"][href]'), function (l) {
      existingHrefs[l.getAttribute('href')] = true;
    });
    Array.prototype.forEach.call(parsedDoc.querySelectorAll('link[rel="stylesheet"][href]'), function (l) {
      var href = l.getAttribute('href');
      if (href && !existingHrefs[href]) {
        var clone = document.createElement('link');
        clone.rel = 'stylesheet';
        clone.href = href;
        head.appendChild(clone);
        existingHrefs[href] = true;
      }
    });

    var existingStyleText = Object.create(null);
    Array.prototype.forEach.call(head.querySelectorAll('style[data-usi-specsheet-copied]'), function (s) {
      existingStyleText[s.textContent] = true;
    });
    Array.prototype.forEach.call(parsedDoc.querySelectorAll('style'), function (s) {
      var text = s.textContent || '';
      if (text.trim() && !existingStyleText[text]) {
        var clone = document.createElement('style');
        clone.setAttribute('data-usi-specsheet-copied', 'true');
        clone.textContent = text;
        head.appendChild(clone);
        existingStyleText[text] = true;
      }
    });
  }

  function fetchSection(href) {
    if (cache[href]) return cache[href];

    cache[href] = fetch(href, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var node = parsed.querySelector(CONTENT_SELECTOR);
        if (!node) throw new Error('Spec sheet content (' + CONTENT_SELECTOR + ') not found in response');
        ensureHeadAssets(parsed);
        return node;
      })
      .catch(function (err) {
        delete cache[href]; // don't cache failures — allow retry on next click
        throw err;
      });

    return cache[href];
  }

  /* ---------------------------------------------------------------------
     Entry point
     --------------------------------------------------------------------- */

  function showSpecSheet(href) {
    openModal();
    setBody('<div class="usi-specsheet-modal__loading" data-usi-specsheet-loading>Loading spec sheet…</div>');

    fetchSection(href).then(function (node) {
      if (modalEl.hidden) return; // user closed it while the fetch was in flight
      var imported = document.importNode(node, true);
      var body = modalEl.querySelector('[data-usi-specsheet-body]');
      body.innerHTML = '';
      body.appendChild(imported);
    }).catch(function (err) {
      console.error('[SpecSheet modal]', err);
      if (modalEl.hidden) return;
      setBody(
        '<div class="usi-specsheet-modal__error">' +
          'Sorry, the spec sheet couldn’t be loaded here.<br>' +
          '<a href="' + href + '" target="_blank" rel="noopener">Open it in a new tab instead</a>' +
        '</div>'
      );
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var link = e.target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    if (!TRIGGER_HREF_MATCH.test(href)) return;

    e.preventDefault();
    showSpecSheet(link.href); // link.href resolves to an absolute URL
  });

  window.USISpecSheetModal = { open: showSpecSheet, close: closeModal };
})();

/* =============================================================================
   INSTALL
   -----------------------------------------------------------------------------
   1. Upload this file to the theme (Shopify admin → Edit code → Assets → Add
      a new asset), alongside assets/usi-specsheet-pdf.js.

   2. Load BOTH scripts globally — on any template that can show the "Spec
      Sheet" link (the product template at minimum; theme.liquid if the link
      can appear elsewhere, e.g. a related-products block). The PDF button
      that ends up injected into the modal needs usi-specsheet-pdf.js's click
      delegate already registered, so load it unconditionally, not lazily:

        {{ 'usi-specsheet-pdf.js' | asset_url | script_tag }}
        {{ 'usi-specsheet-modal.js' | asset_url | script_tag }}

   3. Add usi-specsheet-pdf-button.liquid's button markup to the actual
      template that renders #dvContainer2 (see the note at the top of that
      file — the exact section/snippet name wasn't identified, search Edit
      code for "dvContainer2"), so the fetched HTML includes the button.

   NOTHING else needs to change: the "Spec Sheet" link stays exactly as it is
   today (still a real, working, target="_blank" URL) — this script only
   intercepts the click; if JS fails to load for any reason, the link still
   works as a normal new-tab navigation, so there's no broken-experience
   fallback to build separately.
   ============================================================================= */